import timeOptions from "../../Navbar/timeOptions";
import { getChartTypes } from "../../Navbar/chartTypes";

// Pure builder for the unified chart context menu model (PrimeReact MenuModel).
// Section rules (feature flag `chartContextMenu`):
//   - "Gráfico" is ALWAYS present — mirrors the navbar controls (Símbolo,
//     Período, Estudos, Tipo de gráfico, Cores, Estudos/Objetos Ativos).
//   - "Desenho" / indicator sections are prepended only when the click landed
//     on one — their commands DELEGATE to the existing menus' edit/delete
//     flows, nothing is reimplemented.
// Surface rules (UX review 2026-07-19): quick toggles with SHORT labels stay
// as submenus (Período, Tipo de gráfico); everything with its own richer UI
// opens that UI instead — "Estudos…" the navbar picker modal (search +
// categories + favorites), "Cores…" the shared BarColorModal (long labels
// wrapped inside a submenu). "…" marks every item that opens a surface.

const truncate = (text, max = 32) => {
  if (typeof text !== "string") return text;
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

export const buildChartContextMenuModel = ({ chart, context, handlers }) => {
  const items = [];
  const interactive = context?.interactive || null;
  const indicator = context?.indicator || null;

  // ── Desenho (only when the click landed on a drawing) ────────────────────
  if (interactive) {
    items.push({
      label: "Desenho",
      className: "chart-cm-header",
      disabled: true,
    });
    items.push({
      label: "Editar…",
      icon: "pi pi-pencil",
      command: () => handlers.onEditInteractive(interactive),
    });
    items.push({
      label: "Excluir",
      icon: "pi pi-trash",
      command: () => handlers.onDeleteInteractive(interactive),
    });
    items.push({ separator: true });
  }

  // ── Indicador (only when the click landed on an indicator series) ────────
  if (indicator) {
    items.push({
      label: truncate(indicator.displayName || "Indicador"),
      className: "chart-cm-header",
      disabled: true,
    });
    items.push({
      label: "Editar…",
      icon: "pi pi-pencil",
      command: () => handlers.onEditIndicator(indicator),
    });
    items.push({
      label: "Excluir",
      icon: "pi pi-trash",
      command: () => handlers.onRemoveIndicator(indicator),
    });
    items.push({ separator: true });
  }

  // ── Gráfico (always) ─────────────────────────────────────────────────────
  items.push({
    label: "Gráfico",
    className: "chart-cm-header",
    disabled: true,
  });

  // "Trocar símbolo…" (UX review): verb + object, short — the current symbol
  // is already visible on the tab/navbar, repeating it here only added noise.
  items.push({
    label: "Trocar símbolo…",
    icon: "pi pi-search",
    command: () => handlers.onSelectSymbol(),
  });

  // Período — same options module as PeriodicityDropdown
  const periodicityItems = [];
  for (const opt of timeOptions) {
    if (opt.separator) {
      periodicityItems.push({ separator: true });
    }
    periodicityItems.push({
      label: opt.label,
      icon: chart?.periodicity === opt.value ? "pi pi-check" : undefined,
      command: () => handlers.onSelectPeriodicity(opt.value),
    });
  }
  items.push({
    label: "Período",
    icon: "pi pi-clock",
    items: periodicityItems,
  });

  // Estudos — opens the navbar picker modal (search + categories + favorites
  // already live there; a nested flyout tree duplicated it with worse UX).
  items.push({
    label: "Estudos…",
    icon: "pi pi-chart-line",
    command: () => handlers.onOpenIndicatorsPicker(),
  });

  // Tipo de gráfico — same list as ChartTypeDropdown (feature-flag aware)
  items.push({
    label: "Tipo de gráfico",
    icon: "pi pi-chart-bar",
    items: getChartTypes().map((ct) => ({
      label: ct.label,
      icon: chart?.chartType === ct.value ? "pi pi-check" : undefined,
      command: () => handlers.onSelectChartType(ct.value),
    })),
  });

  // Cores — opens the shared BarColorModal (long provider labels wrapped to
  // two lines inside a submenu; the modal is the one surface, navbar included).
  items.push({
    label: "Cores…",
    icon: "pi pi-palette",
    command: () => handlers.onOpenBarColors(),
  });

  // Template — mirrors the footer "Template" menu; every action triggers the
  // SAME dialogs (they live in WorkspaceMenus, reached via workspaceTemplateRef).
  items.push({
    label: "Template",
    icon: "pi pi-clone",
    items: [
      {
        label: "Salvar como…",
        icon: "pi pi-save",
        command: () => handlers.onTemplateAction("saveAs"),
      },
      {
        label: "Carregar…",
        icon: "pi pi-folder-open",
        command: () => handlers.onTemplateAction("load"),
      },
      { separator: true },
      {
        label: "Exportar para arquivo…",
        icon: "pi pi-download",
        command: () => handlers.onTemplateAction("exportFile"),
      },
      {
        label: "Importar de arquivo…",
        icon: "pi pi-upload",
        command: () => handlers.onTemplateAction("importFile"),
      },
      { separator: true },
      {
        label: "Importar do WebCharts Antigo…",
        icon: "pi pi-file-import",
        command: () => handlers.onTemplateAction("importLegacy"),
      },
    ],
  });

  items.push({ separator: true });
  items.push({
    label: "Estudos Ativos…",
    icon: "pi pi-sliders-h",
    command: () => handlers.onOpenActiveIndicators(),
  });
  items.push({
    label: "Objetos Ativos…",
    icon: "pi pi-list",
    command: () => handlers.onOpenActiveObjects(),
  });

  return items;
};
